// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';
import { list } from './list';
import { Tooltip } from './tooltip';
import { keyCode, isRtl, prepareUrl } from './common';

/**
 * @class Jsw.LookUp
 * @extends Jsw.Component
 */
export const LookUp = Class.create(Component, {
    _initConfiguration($super, config) {
        $super(config);

        this._name = this._getConfigParam('name', null);
        this._cls = this._getConfigParam('cls', 'lookup');
        this._data = this._getConfigParam('data', []);
        this._currentValue = this._getConfigParam('value', null);
        this._newItemValue = this._getConfigParam('newItemValue', null);
        this._dataUrl = this._getConfigParam('dataUrl', null);
        this._copyTitleValue = this._getConfigParam('copyTitleValue', false);
        this._limit = this._getConfigParam('limit', this._dataUrl ? 10 : list.ITEMS_UNLIMITED);
        this._lookUpParams = this._getConfigParam('lookUpParams', {});

        if (!this._currentValue && this._newItemValue) {
            this._currentValue = this._newItemValue;
        }
    },

    _initComponentElement($super) {
        $super();

        this._valueField = new Element('input', {
            type: 'hidden',
            name: this._name,
        });

        this._componentElement
            .insert(this._valueField);

        this._inputField = new Element('input', {
            type: 'text',
            class: 'form-control',
        });

        this._lookUpButton = new Element('span', { class: 'form-control-icon form-control-icon-action' })
            .insert(new Element('i', { class: 'icon-form-control-search' }));

        this._lookUpControl = new Element('div', { class: 'form-control-group' })
            .insert(this._inputField)
            .insert(this._lookUpButton);

        this._dropdownList = new Element('ul', { class: 'dropdown-menu lookup-dropdown-menu' });

        this._initCustomLookUpComponent();
        if (this._customLookUpComponent) {
            this._dropdownList
                .insert(new Element('li', { class: 'dropdown-header' })
                    .update(this.lmsg('label'))
                );
            this._dropdownList
                .insert(new Element('li')
                    .insert(new Element('div', { class: 'dropdown-menu-content' })
                        .insert(this._lookUpControl)
                    )
                );
        } else {
            this._lookUpControl.addClassName('f-large-size');
            this._componentElement
                .insert(this._lookUpControl);
        }

        this._emptyElement = new Element('li')
            .insert(new Element('div', { class: 'dropdown-menu-content' }).update(this.lmsg('nothingFound')));
        this._dropdownList.insert(this._emptyElement);

        this._componentElement
            .insert(this._dropdownList);

        this._updateValue();

        if (0 < this._data.length) {
            this._updateData(this._data);
        }
        if (this._dataUrl && this._limit === list.ITEMS_UNLIMITED) {
            this._getDataByUrl();
        }
    },

    _initCustomLookUpComponent() {
        if (!this._newItemValue) {
            return;
        }

        this._customLookUpComponent = new Element('div', { class: 'input-group' }).update(
            '<input type="text" readonly="" class="form-control f-large-size" value="">' +
            '<span class="input-group-btn">' +
                '<span type="button" class="btn dropdown-toggle">' +
                    '<button type="button">' +
                        '<span class="caret"></span>' +
                    '</button>' +
                '</span>' +
            '</span>'
        );
        this._componentElement.insert(this._customLookUpComponent);

        this._dropdownList
            .insert(this._createItem(this._newItemValue))
            .insert(new Element('li', { class: 'divider' }));
    },

    _getDataByUrl(filter) {
        this._emptyElement.down('div').update(this.lmsg('loading'));
        new Ajax.Request(prepareUrl(this._dataUrl), {
            method: 'get',
            parameters: Object.extend({
                filter,
                limit: this._limit,
            }, this._lookUpParams),
            onSuccess: function (transport) {
                const response = transport.responseText.evalJSON();
                this._emptyElement.down('div').update(this.lmsg('nothingFound'));
                if ('error' === response.status) {
                    return;
                }

                this._data = response.data;
                this._updateData(this._data);
                this._selectResults(filter, response.itemsCount - this._data.length);
            }.bind(this),
        });
    },

    isEmpty() {
        return null === this._currentValue;
    },

    getValue() {
        return this._currentValue ? this._currentValue.id : null;
    },

    getItemValue() {
        return this._currentValue ? this._currentValue : null;
    },

    getDisplayValue() {
        return this._currentValue ? this._currentValue.title : '';
    },

    _openList(showAll) {
        this._componentElement.addClassName('open');
        this._lookUpButton.down('i').removeClassName('icon-form-control-search').addClassName('icon-form-control-clear');
        this._fixDropdownPosition();

        const filter = showAll ? '' : this._inputField.getValue().toLocaleLowerCase();
        if (this._dataUrl && this._limit !== list.ITEMS_UNLIMITED) {
            this._emptyElement.down('div').update(this.lmsg('loading'));
            if (this._getDataTask) {
                clearTimeout(this._getDataTask);
            }
            this._getDataTask = setTimeout(function () {
                this._getDataByUrl(filter);
            }.bind(this), 250);
        } else {
            this._selectResults(filter);
        }
    },

    _closeList() {
        this._updateValue();
        this._componentElement.removeClassName('open');
        this._lookUpButton.down('i').addClassName('icon-form-control-search').removeClassName('icon-form-control-clear');
    },

    _updateValue() {
        this._valueField.setValue(this._currentValue ? (this._copyTitleValue ? this._currentValue.title : this._currentValue.id) : '');
        this._inputField.setValue(this._currentValue && !(this._newItemValue && this._currentValue.id === this._newItemValue.id) ? this._currentValue.title : '');
        if (this._newItemValue) {
            this._componentElement.down('.input-group input').setValue(this._currentValue ? this._currentValue.title : '');
        }
    },

    _selectResults(filter, itemsCount) {
        let itemsFound = 0;
        this._dropdownList.select('li').each(function (item) {
            if (!item._item) {
                return;
            }
            if (this._newItemValue && this._newItemValue.id === item._item.id) {
                if (this._currentValue && this._currentValue.id === this._newItemValue.id) {
                    item.hide();
                    item.next().hide();
                } else {
                    item.show();
                    item.next().show();
                }
                return;
            }
            item.removeClassName('active');
            const itemLink = item.select('a').first();
            let itemData = itemLink.innerHTML.stripTags();
            const pos = itemData.toLowerCase().indexOf(filter);
            if (-1 !== pos) {
                itemsFound++;
                if (itemsFound <= this._limit) {
                    item.show();
                    itemData = `${
                        itemData.substr(0, pos)
                    }<b class="search-result-label">${
                        itemData.substr(pos, filter.length)
                    }</b>${
                        itemData.substr(pos + filter.length)
                    }`;
                } else {
                    item.hide();
                }
            } else {
                item.hide();
            }
            itemLink.update(itemData);
        }.bind(this));

        this._emptyElement.toggle(!itemsFound);
        if (itemsFound) {
            const firstVisibleItem = this._dropdownList.select('li').find(function (el) {
                return el.visible();
            });
            if (firstVisibleItem) {
                firstVisibleItem.addClassName('active');
            }
        }

        itemsFound += itemsCount || 0;

        if (itemsFound > this._limit) {
            this._searchMoreElement.down('div').update(this._getSearchMoreText(itemsFound - this._limit));
            this._searchMoreElement.show();
            this._searchMoreElement.previous().show();
        } else {
            this._searchMoreElement.hide();
            this._searchMoreElement.previous().hide();
        }

        this._fixDropdownPosition();
    },

    _addEvents($super) {
        $super();

        if (this._customLookUpComponent) {
            this._customLookUpComponent.observe('click', function (event) {
                event.preventDefault();
                Tooltip.hide();
                if (this._componentElement.hasClassName('open')) {
                    this._inputField.blur();
                } else {
                    this._openList(true);
                    this._inputField.focus();
                }
            }.bindAsEventListener(this));
        } else {
            this._inputField.observe('focus', this._inputOnFocus.bindAsEventListener(this));
        }
        this._inputField.observe('paste', this._inputOnPaste.bindAsEventListener(this));
        this._inputField.observe('blur', this._inputOnBlur.bindAsEventListener(this));
        this._inputField.observe('keyup', this._inputOnKeyUp.bindAsEventListener(this));
        this._inputField.observe('keydown', this._inputOnKeyDown.bindAsEventListener(this));
        this._lookUpButton.observe('click', this._lookUpButtonOnClick.bindAsEventListener(this));
    },

    _inputOnFocus() {
        this._openList(true);
    },

    _inputOnBlur(event) {
        // workaround for Google Chrome
        setTimeout(function () {
            this._closeList();
        }.bind(this), 300);
    },

    _inputOnPaste(event) {
        setTimeout(function () {
            this._openList();
            const activeItem = this._dropdownList.select('li.active').first();
            if (activeItem) {
                this._currentValue = activeItem._item;
                this._updateValue();
            }
        }.bind(this), 300);
    },

    _inputOnKeyUp(event) {
        if ([keyCode.UP_ARROW, keyCode.DOWN_ARROW, keyCode.LEFT_ARROW, keyCode.RIGHT_ARROW, keyCode.ENTER, keyCode.ESC].indexOf(event.keyCode) !== -1) {
            return;
        }

        this._openList();
    },

    _inputOnKeyDown(event) {
        if ([keyCode.UP_ARROW, keyCode.DOWN_ARROW].indexOf(event.keyCode) !== -1) {
            this._onArrowKeyPressed(event.keyCode);
        }

        if (keyCode.ESC === event.keyCode) {
            this._closeList();
        }

        if (keyCode.ENTER === event.keyCode) {
            const activeItem = this._dropdownList.select('li.active').first();
            if (activeItem) {
                this._currentValue = activeItem._item;
                this._closeList();
                this._componentElement.fire('component:change');
            } else {
                this._closeList();
                this._openList();
            }
        }
    },

    _onArrowKeyPressed(_keyCode) {
        let nextItem = null;
        const activeItem = this._dropdownList.select('li.active').first();
        if (!activeItem) {
            nextItem = this._dropdownList.select('li').first();
            while (nextItem && !(nextItem.visible() && nextItem._item)) {
                nextItem = nextItem.next();
            }
        } else if (keyCode.DOWN_ARROW === _keyCode) {
            nextItem = activeItem.next();
            while (nextItem && !(nextItem.visible() && nextItem._item)) {
                nextItem = nextItem.next();
            }
        } else if (keyCode.UP_ARROW === _keyCode) {
            nextItem = activeItem.previous();
            while (nextItem && !(nextItem.visible() && nextItem._item)) {
                nextItem = nextItem.previous();
            }
        }
        if (nextItem && nextItem.visible() && nextItem._item) {
            nextItem.addClassName('active');
            if (activeItem) {
                activeItem.removeClassName('active');
            }
            this._scrollDropdownList();
        }
    },

    _scrollDropdownList() {
        const firstItem = this._dropdownList.select('li.dropdown-menu-list-item').first();
        const activeItem = this._dropdownList.select('li.active').first();

        if (!firstItem || !activeItem) {
            return;
        }

        const position = Math.round(activeItem.offsetTop / firstItem.offsetHeight) + 1;
        const itemsInFrame = Math.round(this._dropdownList.clientHeight / firstItem.offsetHeight);

        this.frameStart = this.frameStart || 1;

        if (position >= this.frameStart + itemsInFrame) {
            this._dropdownList.scrollTop += firstItem.offsetHeight;
            this.frameStart++;
        } else if (position <= this.frameStart) {
            this._dropdownList.scrollTop -= firstItem.offsetHeight;
            this.frameStart--;
        }
    },

    _lookUpButtonOnClick(event) {
        event.preventDefault();

        if (this._componentElement.hasClassName('open')) {
            this._closeList();
        } else {
            this._openList(true);
        }
    },

    _itemOnClick(event) {
        event.preventDefault();
        this._currentValue = event.target.up('li')._item;
        this._closeList();
        this._componentElement.fire('component:change');
    },

    _itemOnOver() {
        this._dropdownList.select('li.active').each(function (element) {
            element.removeClassName('active');
        });
    },

    _createItem(item) {
        const listItem = new Element('li', { class: 'dropdown-menu-list-item' }).insert(
            new Element('a', { href: '#' }).update(item.title.escapeHTML())
        );
        listItem._item = item;
        listItem.observe('click', this._itemOnClick.bindAsEventListener(this));
        listItem.observe('mouseover', this._itemOnOver.bindAsEventListener(this));

        return listItem;
    },

    _getSearchMoreText(count) {
        return this.lmsg('moreObjectsAvailable', { count });
    },

    _updateData(data) {
        this._clearData();

        data.each(function (item) {
            this._dropdownList.insert(this._createItem(item));
        }.bind(this));

        this._addSearchMoreElement();
    },

    _clearData() {
        while (this._emptyElement.next()) {
            this._emptyElement.next().remove();
        }
    },

    _addSearchMoreElement() {
        this._searchMoreElement = new Element('li')
            .insert(new Element('div', { class: 'dropdown-menu-content' }));
        this._dropdownList
            .insert(new Element('li', { class: 'divider' }).hide())
            .insert(this._searchMoreElement.hide());
    },

    _fixDropdownPosition() {
        const style = {};
        style[isRtl() ? 'right' : 'left'] = null;
        this._dropdownList.setStyle(style);

        const windowWidth = $(document.body).getWidth();
        const width = this._dropdownList.getWidth();
        const offsets = this._dropdownList.cumulativeOffset();
        const delta = (isRtl() ? offsets.left : (windowWidth - width - offsets.left))
            - parseInt($$('.page-content')[0].getStyle('padding-left'));

        if (delta < 0) {
            style[isRtl() ? 'right' : 'left'] = `${delta}px`;
            this._dropdownList.setStyle(style);
        }
    },
});



// WEBPACK FOOTER //
// ./jsw/lookup.js